import React from 'react';
import { Mail, Instagram, Facebook, Linkedin, ThumbsUp, MessageCircle, Repeat2, Heart, Bookmark, Send, MoreHorizontal, Globe, Thermometer, Reply, Forward, Star } from 'lucide-react';

/**
 * "What you actually get" — four channels, four executions.
 * The four cards are intentionally styled to *look* like each platform.
 * Do not unify their layouts.
 */
export default function ChannelExecutions() {
  return (
    <section
      id="channel-executions"
      data-testid="channel-executions-section"
      className="pt-10 pb-4 sm:pt-14 sm:pb-6 lg:pt-16 lg:pb-8"
      style={{ backgroundColor: '#FAFAF9' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <p className="uppercase tracking-[0.16em] text-[11px] sm:text-xs font-semibold mb-3 sm:mb-4" style={{ color: '#5132d4' }}>
          What you actually get
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] leading-tight max-w-4xl" style={{ color: '#130F28' }}>
          One campaign. Four channels. <span className="italic" style={{ color: '#5132d4' }}>Four different executions.</span>
        </h2>
        <p className="mt-4 sm:mt-5 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ color: '#3b3656' }}>
          We don't write one post and paste it everywhere. Every channel gets content built for the people who use it, every week, all year.
        </p>
        <ul
          data-testid="channel-cadence-list"
          className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm"
          style={{ color: '#1f1c33' }}
        >
          <li className="flex items-center gap-2"><Mail size={14} aria-hidden="true" style={{ color: '#6B4BEF' }} /><span><span className="font-semibold">Email:</span> ~55 a year</span></li>
          <li className="flex items-center gap-2"><Instagram size={14} aria-hidden="true" style={{ color: '#E1306C' }} /><span><span className="font-semibold">Instagram:</span> 3 posts a week</span></li>
          <li className="flex items-center gap-2"><Facebook size={14} aria-hidden="true" style={{ color: '#1877F2' }} /><span><span className="font-semibold">Facebook:</span> 3 posts a week</span></li>
          <li className="flex items-center gap-2"><Linkedin size={14} aria-hidden="true" style={{ color: '#0A66C2' }} /><span><span className="font-semibold">LinkedIn:</span> 2 posts a week</span></li>
        </ul>
        <p className="mt-3 text-xs sm:text-sm" style={{ color: '#8a85a3' }}>
          Example campaign: <span className="italic" style={{ color: '#4a4566' }}>"Before the first freeze"</span>
          <span className="mx-2">·</span>
          Apex HVAC, Charleston SC
        </p>

        {/* 2x2 grid — cards size to their own content, no forced stretching.
            IG uses a small banner photo so it matches the compact height of Email/FB/LI. */}
        <div className="mt-8 sm:mt-10 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6 items-start">
          <EmailCard />
          <InstagramCard />
          <FacebookCard />
          <LinkedInCard />
        </div>

        <p
          data-testid="channel-executions-disclaimer"
          className="mt-6 sm:mt-8 text-xs sm:text-sm italic"
          style={{ color: '#4a4566' }}
        >
          Example shown. Your campaigns are built from your business and your website.
        </p>
      </div>
    </section>
  );
}

/* --------------------------------- helpers -------------------------------- */

function ChannelLabel({ icon: Icon, name, color, bg }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <span
        aria-hidden="true"
        className="inline-flex w-7 h-7 rounded-lg items-center justify-center"
        style={{ backgroundColor: bg, color }}
      >
        <Icon size={15} strokeWidth={2.25} />
      </span>
      <span className="uppercase tracking-[0.18em] text-[10px] sm:text-[11px] font-bold" style={{ color: '#4a4566' }}>
        {name}
      </span>
    </div>
  );
}

/* ---------------------------------- EMAIL --------------------------------- */
function EmailCard() {
  return (
    <div data-testid="channel-card-email" className="flex flex-col">
      <ChannelLabel icon={Mail} name="Email" color="#6B4BEF" bg="rgba(107,75,239,0.12)" />
      <article
        className="rounded-xl overflow-hidden bg-white border shadow-[0_18px_40px_-24px_rgba(19,15,40,0.28)]"
        style={{ borderColor: '#e6e2d6' }}
      >
        {/* Mail app top chrome */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b" style={{ backgroundColor: '#f5f3ec', borderColor: '#e6e2d6' }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#ff5f57' }} aria-hidden="true" />
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#febc2e' }} aria-hidden="true" />
          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: '#28c840' }} aria-hidden="true" />
          <span className="ml-2 text-[11px] font-medium" style={{ color: '#7a7566' }}>Inbox</span>
        </div>
        {/* Meta rows */}
        <div className="px-5 pt-4 pb-3">
          <div className="flex items-center gap-3 pb-3 border-b" style={{ borderColor: '#efece2' }}>
            <span
              aria-hidden="true"
              className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center font-bold text-white text-xs"
              style={{ background: 'linear-gradient(135deg, #6B4BEF 0%, #5132d4 100%)' }}
            >
              AH
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold truncate" style={{ color: '#130F28' }}>Apex HVAC &amp; Plumbing</p>
              <p className="text-[11px]" style={{ color: '#7a7566' }}>to me · 9:14 AM</p>
            </div>
          </div>
          <p className="mt-3 font-semibold text-[15px] leading-snug" style={{ color: '#130F28' }}>
            Before the first cold night
          </p>
          <p className="mt-3 text-[14px] leading-relaxed" style={{ color: '#1f1c33' }}>
            Every year around this time we get the same call: no heat, coldest night of the year. Here's the ten-minute check that prevents most of them.
          </p>
          <p className="mt-4 text-[13px] font-semibold" style={{ color: '#5132d4' }}>
            Read the check →
          </p>
        </div>

        {/* Engagement counts row — parallels FB/LI */}
        <div className="px-4 pt-1 pb-2 flex items-center gap-2 text-[12px]" style={{ color: '#7a7566' }}>
          <span aria-hidden="true" className="inline-flex w-4 h-4 rounded-full items-center justify-center" style={{ backgroundColor: '#febc2e' }}>
            <Star size={9} className="text-white" strokeWidth={3} />
          </span>
          <span>Starred</span>
          <span className="ml-auto">1 attachment</span>
        </div>

        {/* Divider + action row */}
        <div className="mx-4 border-t" style={{ borderColor: '#efece2' }} />
        <div className="grid grid-cols-3 py-1.5 text-[13px] font-semibold" style={{ color: '#65676B' }}>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f5f3ec] rounded transition">
            <Reply size={16} aria-hidden="true" /> Reply
          </button>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f5f3ec] rounded transition">
            <Forward size={16} aria-hidden="true" /> Forward
          </button>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f5f3ec] rounded transition">
            <Star size={16} aria-hidden="true" /> Star
          </button>
        </div>
      </article>
    </div>
  );
}

/* -------------------------------- INSTAGRAM ------------------------------- */
function InstagramCard() {
  return (
    <div data-testid="channel-card-instagram" className="flex flex-col">
      <ChannelLabel icon={Instagram} name="Instagram" color="#E1306C" bg="rgba(225,48,108,0.10)" />
      <article
        className="rounded-xl overflow-hidden bg-white border shadow-[0_18px_40px_-24px_rgba(19,15,40,0.28)]"
        style={{ borderColor: '#e6e2d6' }}
      >
        {/* Header — matches the FB/LI shape: avatar + name + meta + more */}
        <div className="flex items-center gap-3 px-4 pt-4">
          <span
            aria-hidden="true"
            className="flex-shrink-0 w-11 h-11 rounded-full p-[2px]"
            style={{ background: 'linear-gradient(45deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)' }}
          >
            <span className="w-full h-full rounded-full flex items-center justify-center font-bold text-[12px] text-[#1f1c33] bg-white">
              AH
            </span>
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold leading-tight" style={{ color: '#130F28' }}>apexhvac_chs</p>
            <p className="text-[11px] leading-tight mt-0.5" style={{ color: '#8a85a3' }}>Charleston, SC · 3h</p>
          </div>
          <MoreHorizontal size={18} style={{ color: '#8a85a3' }} aria-hidden="true" />
        </div>

        {/* Body: caption first, then a smaller inline photo (not full square) */}
        <div className="px-4 pt-3 pb-3">
          <p className="text-[14px] leading-relaxed" style={{ color: '#130F28' }}>
            Cold snap's coming 🥶 Three things to check before you need us. Link in bio.
          </p>
          <div
            className="mt-3 relative rounded-lg overflow-hidden flex items-center justify-center h-20"
            style={{ background: 'radial-gradient(circle at 50% 40%, #2a2555 0%, #0f0e1a 65%)' }}
            aria-label="Thermostat reading fifty-eight degrees"
          >
            <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)', backgroundSize: '32px 32px' }} aria-hidden="true" />
            <div className="relative flex items-center gap-2.5">
              <Thermometer size={18} style={{ color: '#10C98F' }} aria-hidden="true" />
              <p className="font-serif text-white text-3xl leading-none tracking-tight">
                58<span className="align-top text-base">°</span>
              </p>
              <div className="flex flex-col leading-tight">
                <span className="text-[9px] tracking-[0.3em] font-semibold" style={{ color: '#10C98F' }}>INDOOR</span>
                <span className="text-[9px] tracking-[0.3em] font-semibold mt-0.5" style={{ color: '#10C98F' }}>6:02 AM</span>
              </div>
            </div>
          </div>
          <p className="mt-2 text-[13px] font-semibold" style={{ color: '#00376B' }}>
            #CharlestonSC #HVAC #WinterReady
          </p>
        </div>

        {/* Engagement counts — same shape as FB/LI */}
        <div className="px-4 pt-1 pb-2 flex items-center gap-2 text-[12px]" style={{ color: '#8a85a3' }}>
          <span aria-hidden="true" className="inline-flex w-4 h-4 rounded-full items-center justify-center" style={{ background: 'linear-gradient(45deg, #F58529 0%, #DD2A7B 50%, #8134AF 100%)' }}>
            <Heart size={9} className="text-white" strokeWidth={3} />
          </span>
          <span>218 likes</span>
          <span className="ml-auto">14 comments</span>
        </div>

        {/* Divider + actions row */}
        <div className="mx-4 border-t" style={{ borderColor: '#e6e2d6' }} />
        <div className="grid grid-cols-4 py-1.5 text-[13px] font-semibold" style={{ color: '#65676B' }}>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f5f3ec] rounded transition">
            <Heart size={16} aria-hidden="true" /> Like
          </button>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f5f3ec] rounded transition">
            <MessageCircle size={16} aria-hidden="true" /> Comment
          </button>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f5f3ec] rounded transition">
            <Send size={16} aria-hidden="true" /> Send
          </button>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f5f3ec] rounded transition">
            <Bookmark size={16} aria-hidden="true" /> Save
          </button>
        </div>
      </article>
    </div>
  );
}

/* -------------------------------- FACEBOOK -------------------------------- */
function FacebookCard() {
  return (
    <div data-testid="channel-card-facebook" className="flex flex-col">
      <ChannelLabel icon={Facebook} name="Facebook" color="#1877F2" bg="rgba(24,119,242,0.10)" />
      <article
        className="rounded-xl overflow-hidden bg-white border shadow-[0_18px_40px_-24px_rgba(19,15,40,0.28)]"
        style={{ borderColor: '#e6e2d6' }}
      >
        {/* Profile row */}
        <div className="flex items-center gap-3 px-4 pt-4">
          <span
            aria-hidden="true"
            className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center font-bold text-white text-sm"
            style={{ background: 'linear-gradient(135deg, #1877F2 0%, #0a4bb8 100%)' }}
          >
            AH
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold leading-tight" style={{ color: '#050505' }}>Apex HVAC &amp; Plumbing</p>
            <p className="text-[11px] flex items-center gap-1 mt-0.5" style={{ color: '#65676B' }}>
              <span>2h</span>
              <span>·</span>
              <Globe size={11} aria-hidden="true" />
            </p>
          </div>
          <MoreHorizontal size={18} style={{ color: '#65676B' }} aria-hidden="true" />
        </div>

        {/* Body */}
        <div className="px-4 pt-3 pb-3">
          <p className="text-[14px] leading-relaxed" style={{ color: '#050505' }}>
            Charleston neighbors, the first real cold night usually hits the week after Thanksgiving, and every year our phones light up. Three things you can check this weekend so you're not on that list.
          </p>
        </div>

        {/* Engagement pill */}
        <div className="px-4 pt-1 pb-2 flex items-center gap-1 text-[12px]" style={{ color: '#65676B' }}>
          <span aria-hidden="true" className="inline-flex w-4 h-4 rounded-full items-center justify-center" style={{ backgroundColor: '#1877F2' }}>
            <ThumbsUp size={9} className="text-white" strokeWidth={3} />
          </span>
          <span>142</span>
          <span className="ml-auto">18 comments · 6 shares</span>
        </div>

        {/* Divider + actions */}
        <div className="mx-4 border-t" style={{ borderColor: '#e6e2d6' }} />
        <div className="grid grid-cols-3 py-1.5 text-[13px] font-semibold" style={{ color: '#65676B' }}>
          <button type="button" className="flex items-center justify-center gap-2 py-1.5 hover:bg-[#f0f2f5] rounded transition">
            <ThumbsUp size={16} aria-hidden="true" /> Like
          </button>
          <button type="button" className="flex items-center justify-center gap-2 py-1.5 hover:bg-[#f0f2f5] rounded transition">
            <MessageCircle size={16} aria-hidden="true" /> Comment
          </button>
          <button type="button" className="flex items-center justify-center gap-2 py-1.5 hover:bg-[#f0f2f5] rounded transition">
            <Repeat2 size={16} aria-hidden="true" /> Share
          </button>
        </div>
      </article>
    </div>
  );
}

/* -------------------------------- LINKEDIN -------------------------------- */
function LinkedInCard() {
  return (
    <div data-testid="channel-card-linkedin" className="flex flex-col">
      <ChannelLabel icon={Linkedin} name="LinkedIn" color="#0A66C2" bg="rgba(10,102,194,0.10)" />
      <article
        className="rounded-xl overflow-hidden bg-white border shadow-[0_18px_40px_-24px_rgba(19,15,40,0.28)]"
        style={{ borderColor: '#e6e2d6' }}
      >
        {/* Profile row: square logo, name, descriptor, timestamp */}
        <div className="flex items-start gap-3 px-4 pt-4">
          <span
            aria-hidden="true"
            className="flex-shrink-0 w-12 h-12 rounded-md flex items-center justify-center font-bold text-white text-sm"
            style={{ background: 'linear-gradient(135deg, #0A66C2 0%, #073e78 100%)' }}
          >
            AH
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-semibold leading-tight" style={{ color: '#000000' }}>Apex HVAC &amp; Plumbing</p>
            <p className="text-[11px] leading-tight mt-0.5" style={{ color: '#00000099' }}>
              Commercial &amp; residential HVAC, Charleston SC
            </p>
            <p className="text-[11px] mt-1 flex items-center gap-1" style={{ color: '#00000099' }}>
              <span>1d</span>
              <span>·</span>
              <Globe size={11} aria-hidden="true" />
            </p>
          </div>
          <MoreHorizontal size={18} style={{ color: '#00000099' }} aria-hidden="true" />
        </div>

        {/* Body */}
        <div className="px-4 pt-3 pb-3">
          <p className="text-[14px] leading-relaxed" style={{ color: '#000000' }}>
            Emergency HVAC calls spike sharply in the first week of sustained cold, and most of them are preventable. For property managers, a preventive check in November costs a fraction of an emergency call in January.
          </p>
          <p className="mt-2 text-[13px] font-semibold" style={{ color: '#0A66C2' }}>
            #FacilitiesManagement #HVAC #PreventiveMaintenance
          </p>
        </div>

        {/* Engagement counts */}
        <div className="px-4 pt-1 pb-2 flex items-center gap-2 text-[12px]" style={{ color: '#00000099' }}>
          <span aria-hidden="true" className="inline-flex w-4 h-4 rounded-full items-center justify-center" style={{ backgroundColor: '#0A66C2' }}>
            <ThumbsUp size={9} className="text-white" strokeWidth={3} />
          </span>
          <span>Priya S. and 87 others</span>
          <span className="ml-auto">12 comments · 4 reposts</span>
        </div>

        {/* Divider + actions */}
        <div className="mx-4 border-t" style={{ borderColor: '#e6e2d6' }} />
        <div className="grid grid-cols-4 py-1.5 text-[12px] font-semibold" style={{ color: '#00000099' }}>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f3f2ef] rounded transition">
            <ThumbsUp size={14} aria-hidden="true" /> Like
          </button>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f3f2ef] rounded transition">
            <MessageCircle size={14} aria-hidden="true" /> Comment
          </button>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f3f2ef] rounded transition">
            <Repeat2 size={14} aria-hidden="true" /> Repost
          </button>
          <button type="button" className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-[#f3f2ef] rounded transition">
            <Send size={14} aria-hidden="true" /> Send
          </button>
        </div>
      </article>
    </div>
  );
}
